import { validateInventorizationRegisterReadings } from '@/features/inventorization/domain/inventorization-reading-validation';
import {
  createInventorizationRegisterId,
  createInventorizationRegisterLabel,
  DEFAULT_INVENTORIZATION_REGISTER_ID,
} from '@/features/inventorization/domain/inventorization-registers';
import type { LocalPhotoRef } from '@/shared/capture/capture-session';
import {
  BLOCK_STATUSES,
  type BlockStatus,
  type CaptureSession,
  PHOTO_CONTEXTS,
  type ValidationIssue,
} from '@/shared/capture/capture-session';

export interface InventorizationMainReading {
  counter: string;
  obis: string;
  tariff: string;
  unit: string;
}

export interface InventorizationRegisterReading extends InventorizationMainReading {
  id: string;
  label: string;
  photoRef: string;
}

export const emptyInventorizationMainReading: InventorizationMainReading = {
  counter: '',
  obis: '',
  tariff: '',
  unit: '',
};

export const emptyInventorizationRegisterReading: InventorizationRegisterReading = {
  ...emptyInventorizationMainReading,
  id: DEFAULT_INVENTORIZATION_REGISTER_ID,
  label: createInventorizationRegisterLabel(0),
  photoRef: DEFAULT_INVENTORIZATION_REGISTER_ID,
};

export function readInventorizationMainReading(session: CaptureSession): InventorizationMainReading {
  const [reading] = readInventorizationRegisters(session);

  return {
    counter: reading?.counter ?? '',
    obis: reading?.obis ?? '',
    tariff: reading?.tariff ?? '',
    unit: reading?.unit ?? '',
  };
}

export function readInventorizationRegisters(session: CaptureSession): InventorizationRegisterReading[] {
  const inventorization = session.data.inventorization as { registers?: unknown[] } | undefined;
  const removedRegisterIds = readInventorizationRemovedRegisterIds(session);
  const registers = toRegisterArray(inventorization?.registers).filter(
    register => !isRemovedRegister(register, removedRegisterIds),
  );

  if (registers.length > 0) {
    return registers;
  }

  const photoReadings = createRegisterReadingsFromPhotos(
    session.photos,
    removedRegisterIds,
    emptyInventorizationRegisterReading,
  );

  if (photoReadings.length > 0) {
    return photoReadings;
  }

  return [emptyInventorizationRegisterReading];
}

export function readInventorizationRemovedRegisterIds(session: CaptureSession): string[] {
  const inventorization = session.data.inventorization as { removedRegisterIds?: unknown[] } | undefined;

  return (Array.isArray(inventorization?.removedRegisterIds) ? inventorization.removedRegisterIds : []).filter(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  );
}

export function validateInventorizationMainReading(session: CaptureSession): ValidationIssue[] {
  return validateInventorizationRegisterReadings(session, readInventorizationRegisters(session));
}

export function getInventorizationReadingBlockStatus(session: CaptureSession): BlockStatus {
  return validateInventorizationMainReading(session).length === 0 ? BLOCK_STATUSES.valid : BLOCK_STATUSES.inProgress;
}

function toRegisterArray(value: unknown): InventorizationRegisterReading[] {
  return (Array.isArray(value) ? value : []).map((register, index) => toRegister(register, index));
}

function createRegisterReadingsFromPhotos(
  photos: LocalPhotoRef[],
  removedRegisterIds: string[],
  fallback: InventorizationRegisterReading,
) {
  const registerPhotos = photos
    .filter(photo => photo.context === PHOTO_CONTEXTS.register)
    .filter(photo => !removedRegisterIds.includes(photo.registerId ?? DEFAULT_INVENTORIZATION_REGISTER_ID))
    .toSorted((left, right) => left.capturedAt.localeCompare(right.capturedAt));

  return registerPhotos.map((photo, index) => ({
    ...fallback,
    id: photo.registerId ?? createInventorizationRegisterId(index),
    label: createInventorizationRegisterLabel(index),
    photoRef: photo.registerId ?? createInventorizationRegisterId(index),
  }));
}

function isRemovedRegister(register: InventorizationRegisterReading, removedRegisterIds: string[]) {
  return removedRegisterIds.includes(register.id) || removedRegisterIds.includes(register.photoRef);
}

function toRegister(value: unknown, index: number): InventorizationRegisterReading {
  const record = value as Partial<InventorizationRegisterReading>;
  const id = record.id || record.photoRef || createInventorizationRegisterId(index);

  return {
    counter: record.counter ?? '',
    id,
    label: record.label || createInventorizationRegisterLabel(index),
    obis: record.obis ?? '',
    photoRef: record.photoRef || id,
    tariff: record.tariff ?? '',
    unit: record.unit ?? '',
  };
}
